import { Link as RouterLink } from "react-router";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { routePaths } from "../../../Router/router";
import { CellSize } from "../../Table/table";
import { Box, Chip, Link, Stack, Typography } from "@mui/material";
import { cacheService, thumbnailService } from "../../../Core/Api/services";
import moment from "moment";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import { FolderOutlined } from "@mui/icons-material";
import AssetTypeChip from "../../Misc/Chips/AssetChip/asset-type-chip";
import { DriveActions } from "../Common/drive-actions";
import { CardThumbnail } from "../../Cards/AssetCard/asset-card-body";
import { AssetTypeAlphaColor } from "../../../Utils/helpers";
import { Img } from "react-image";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import AssetThumbnail from "../../Misc/AssetThumbnail";
import { useTranslation } from "react-i18next";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";

export const useDriveTableCols = () => {
  const { t } = useTranslation();
  type Row = DbAssetObjectInfos[][number];

  // Cols def
  const cols: GridColDef[] = [
    {
      field: "name",
      headerName: t("common:name"),
      minWidth: CellSize.xlarge,
      flex: CellSize.xlarge,
      renderCell: (data: GridRenderCellParams<Row>) => {
        const backgroundColor = `linear-gradient(225.52deg, ${AssetTypeAlphaColor(
          data?.row?.type,
          0.12
        )} 0%, ${AssetTypeAlphaColor(data?.row?.type, 0.02)} 80.71%, ${AssetTypeAlphaColor(
          data?.row?.type,
          0.08
        )} 100%)`;
        return (
          <Stack gap={2} direction="row" alignItems={"center"}>
            <Box sx={{ height: 40, aspectRatio: 16 / 9, position: "relative" }}>
              <CardThumbnail backgroundColor={backgroundColor}>
                {![ObjectType.Audio, ObjectType.AudioClip, ObjectType.Subtitle].includes(data?.row?.type) && (
                  <Img
                    style={{
                      position: "relative",
                      overflow: "hidden",
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      zIndex: 2,
                    }}
                    src={thumbnailService.GetThumbRefByObjectId(
                      data?.row?.id,
                      cacheService.GetAuthKey()!,
                      ThumbnailSize.xmedium
                    )}
                  />
                )}
                <Box
                  sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 1 }}
                >
                  <AssetThumbnail type={data?.row?.type} size="micro" translucent />
                </Box>
              </CardThumbnail>
            </Box>
            <Typography sx={(t) => ({ ...t.helpers(t).clampedText })} variant="body1">
              {data?.row?.name}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "type",
      headerName: t("common:infos"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      filterable: false,
      valueGetter: (_, row: Row) => {
        let duration: string | null = GetMetadataByTagName(row, "durationSMPTE")?.value ?? null;
        let wrapper: string | null = GetMetadataByTagName(row, "techWrapper")?.value ?? null;

        if (duration) {
          if (duration.substring(0, 2) === "00") duration = `${duration.substring(3, 5)}:${duration.substring(6, 8)}`;
          else duration = `${parseInt(duration.substring(0, 2))}h${duration.substring(3, 5)}`;
        }

        return { duration: duration, wrapper: wrapper, asset: row };
      },
      valueFormatter: (value: { duration?: string; wrapper?: string; asset: DbAssetObjectInfos }) => {
        if (value.asset.type === ObjectType.Folder || value.asset.type === ObjectType.Group)
          return value.asset.childCount;
        return value.asset.type;
      },
      renderCell: (data: GridRenderCellParams<Row>) => {
        return (
          <Stack direction="row" spacing={0.5} pt={0.5}>
            {data?.row?.type === ObjectType.Folder || data?.row?.type === ObjectType.Group ? (
              <Chip
                size="small"
                color="accent"
                variant="outlined"
                label={data?.row?.childCount}
                icon={<FolderOutlined />}
              />
            ) : (
              <AssetTypeChip
                size="small"
                variant="outlined"
                label={data.value.wrapper ?? undefined}
                type={data?.row?.type}
              />
            )}
            {data.value.duration ? <Chip size="small" label={data.value.duration} /> : <></>}
          </Stack>
        );
      },
    },
    {
      field: "id",
      headerName: "Id",
      minWidth: CellSize.small,
      flex: CellSize.small,
      renderCell: (data: GridRenderCellParams<Row>) => (
        <Link
          color="primary"
          underline="hover"
          target="_blank"
          component={RouterLink}
          to={`${routePaths.drive.path}/${data?.row?.id}`}
        >
          #{data.value}
        </Link>
      ),
    },
    {
      field: "creationInfos.date",
      headerName: t("common:upperCaseCreation"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      type: "date",
      valueGetter: (_, row: Row) => (row.creationInfos?.date === null ? null : new Date(row.creationInfos?.date!)),
      valueFormatter: (value: Date) => moment(value).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS),
      renderCell: (data: GridRenderCellParams<Row>) => (
        <Typography variant="body1" color="text.secondary">
          {data.formattedValue}
        </Typography>
      ),
    },
    {
      field: "driveActions",
      type: "actions",
      align: "right",
      minWidth: CellSize.actionsXsmall,
      flex: CellSize.actionsXsmall,
      renderCell: (data: GridRenderCellParams<Row>) => {
        return <DriveActions objId={data?.row?.id} object={data.row} context="table" />;
      },
    },
  ];

  return cols;
};
